import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';
import { Search, User, Filter, MapPin, ChevronRight } from 'lucide-react';

const EVENT_LABEL = {
  logout: 'Signed out',
  login: 'Signed in',
  search_performed: 'Searched',
  conversation_started: 'Started chat',
  profile_viewed: 'Viewed profile',
  profile_viewed_by: 'Profile viewed',
  availability_changed: 'Availability',
  phone_revealed: 'Phone revealed',
  phone_reveal_denied: 'Phone reveal denied',
  contact_requested: 'Contact requested',
  contact_request_decided: 'Contact decided',
  account_created: 'Account created',
  email_verified: 'Email verified',
  profile_completed: 'Profile completed',
  worker_became_searchable: 'Became searchable',
};

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
    });
  } catch {
    return iso;
  }
}

function EventPill({ eventType }) {
  const label = EVENT_LABEL[eventType] || eventType;
  const colour = {
    search_performed: 'bg-blue-50 text-blue-700 border-blue-200',
    conversation_started: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    phone_revealed: 'bg-amber-50 text-amber-700 border-amber-200',
    phone_reveal_denied: 'bg-red-50 text-red-700 border-red-200',
    contact_requested: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    login: 'bg-slate-50 text-slate-700 border-slate-200',
    logout: 'bg-slate-50 text-slate-500 border-slate-200',
  }[eventType] || 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <span className={`inline-flex items-center text-xs font-medium rounded-full border px-2 py-0.5 ${colour}`} data-testid={`activity-event-pill-${eventType}`}>
      {label}
    </span>
  );
}

function RowCard({ row, onOpenUser }) {
  const search = row.event_type === 'search_performed' ? row.meta || {} : null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start" data-testid="activity-row">
      <div className="flex-1 min-w-0 w-full">
        <div className="flex items-center flex-wrap gap-2 mb-1">
          <EventPill eventType={row.event_type} />
          <span className="text-xs text-slate-400">{formatTime(row.created_at)}</span>
          {row.user_type && (
            <span className="text-xs uppercase tracking-wide text-slate-400">{row.user_type}</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => row.user_id && onOpenUser(row.user_id)}
          className="font-semibold text-slate-900 text-sm hover:underline flex items-center gap-1"
          data-testid="activity-user-name"
        >
          <User className="h-3.5 w-3.5 text-slate-400" />
          {row.user_name || row.user_id || '—'}
          {row.business_name && <span className="text-slate-500 font-normal">· {row.business_name}</span>}
        </button>
        {search && (
          <div className="mt-1 text-sm text-slate-600 flex flex-wrap gap-x-3 gap-y-1" data-testid="activity-search-detail">
            {search.trade && <span><Search className="inline h-3 w-3 mr-0.5" />{search.trade}</span>}
            {search.outward_postcode && <span><MapPin className="inline h-3 w-3 mr-0.5" />{search.outward_postcode}</span>}
            <span className={search.zero_results ? 'text-red-600' : 'text-slate-500'}>
              {search.results_count} result{search.results_count === 1 ? '' : 's'}
            </span>
          </div>
        )}
        {row.event_type === 'conversation_started' && row.meta?.target_user_id && (
          <div className="mt-1 text-sm text-slate-600">→ with {row.target_user_name || row.meta.target_user_id}</div>
        )}
        {row.event_type === 'phone_revealed' && (
          <div className="mt-1 text-sm text-slate-600">→ {row.target_user_name || row.meta?.target_user_id} ({row.meta?.mode})</div>
        )}
        {row.event_type === 'phone_reveal_denied' && (
          <div className="mt-1 text-sm text-red-600">Rate limit — retry in {row.meta?.retry_after_seconds}s</div>
        )}
        {row.event_type === 'availability_changed' && (
          <div className="mt-1 text-sm text-slate-600">{row.meta?.action} · {row.meta?.date_blocked}</div>
        )}
        {row.event_type === 'profile_viewed' && (
          <div className="mt-1 text-sm text-slate-600">→ {row.target_user_name || row.meta?.target_user_id}</div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300 hidden sm:block mt-1" />
    </div>
  );
}

export default function AdminActivityFeed({ onOpenUser }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [filters, setFilters] = useState({
    user_id: '', user_type: '', event_type: '', days: '14',
  });

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.days) {
        const from = new Date(Date.now() - parseInt(filters.days, 10) * 86400000).toISOString();
        params.append('from', from);
      }
      params.append('limit', '100');
      const res = await axios.get(`${BACKEND_URL}/api/admin/activity/feed?${params.toString()}`, { withCredentials: true });
      setRows(res.data.rows || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/activity/event-types`, { withCredentials: true });
        setEventTypes(res.data.event_types || []);
      } catch { /* leave empty */ }
    })();
  }, []);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  return (
    <div className="space-y-4" data-testid="admin-activity-feed">
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-orange-500" />
          <h3 className="font-semibold text-slate-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input
            type="text" placeholder="User ID (e.g. user_abc…)"
            value={filters.user_id}
            onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value.trim() }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            data-testid="activity-filter-user-id"
          />
          <select
            value={filters.user_type}
            onChange={(e) => setFilters((f) => ({ ...f, user_type: e.target.value }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            data-testid="activity-filter-user-type"
          >
            <option value="">All account types</option>
            <option value="employer">Employers</option>
            <option value="tradesperson">Tradespeople</option>
            <option value="admin">Admins</option>
          </select>
          <select
            value={filters.event_type}
            onChange={(e) => setFilters((f) => ({ ...f, event_type: e.target.value }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            data-testid="activity-filter-event-type"
          >
            <option value="">All event types</option>
            {eventTypes.map((et) => (
              <option key={et} value={et}>{EVENT_LABEL[et] || et}</option>
            ))}
          </select>
          <select
            value={filters.days}
            onChange={(e) => setFilters((f) => ({ ...f, days: e.target.value }))}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            data-testid="activity-filter-days"
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading activity…</div>}
      {error && <div className="text-sm text-red-600" data-testid="activity-error">{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-6 text-center" data-testid="activity-empty">
          No activity in the selected window.
        </div>
      )}

      <div className="space-y-2">
        {rows.map((r, i) => (
          <RowCard key={`${r.source}-${r.created_at}-${i}`} row={r} onOpenUser={onOpenUser} />
        ))}
      </div>
    </div>
  );
}
